import { useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api.js';
import { useNicknames } from '../lib/useNicknames.js';

interface Order {
  order_id: string;
  purchase_date: string;
  last_update_date: string;
  status: string;
  fulfillment_channel: string;
  marketplace: string;
  sku: string;
  asin: string;
  product_name: string;
  quantity: number;
  quantity_shipped: number;
  item_price: number;
  item_tax: number;
  shipping_price: number;
  currency: string;
  buyer_name: string;
  ship_city: string;
  ship_state: string;
  ship_country: string;
}

interface OrdersResponse {
  orders: Order[];
  total: number;
  page: number;
  limit: number;
  start: string;
  end: string;
}

const MARKETPLACE_LABELS: Record<string, string> = {
  ATVPDKIKX0DER: 'US',
  A2EUQ1WTGCTBG2: 'CA',
  A1AM78C64UM0Y8: 'MX',
  A1F83G8C2ARO7P: 'UK',
  A1PA6795UKMFR9: 'DE',
  A13V1IB3VIYZZH: 'FR',
  APJ6JRA9NG5V4: 'IT',
  A1RKKUPIHCS9HS: 'ES',
  A1805IZSGTT6HS: 'NL',
  A2NODRKZP88ZB9: 'SE',
};

const SC_DOMAINS: Record<string, string> = {
  ATVPDKIKX0DER: 'sellercentral.amazon.com',
  A2EUQ1WTGCTBG2: 'sellercentral.amazon.ca',
  A1AM78C64UM0Y8: 'sellercentral.amazon.com.mx',
  A1F83G8C2ARO7P: 'sellercentral.amazon.co.uk',
  A1PA6795UKMFR9: 'sellercentral.amazon.de',
  A13V1IB3VIYZZH: 'sellercentral.amazon.fr',
  APJ6JRA9NG5V4: 'sellercentral.amazon.it',
  A1RKKUPIHCS9HS: 'sellercentral.amazon.es',
  A1805IZSGTT6HS: 'sellercentral.amazon.nl',
  A2NODRKZP88ZB9: 'sellercentral.amazon.se',
};

function scOrderUrl(marketplace: string, orderId: string) {
  const domain = SC_DOMAINS[marketplace] ?? 'sellercentral.amazon.com';
  return `https://${domain}/orders-v3/order/${orderId}`;
}

const STATUS_COLORS: Record<string, string> = {
  Shipped: 'bg-green-100 text-green-700',
  Unshipped: 'bg-yellow-100 text-yellow-700',
  Pending: 'bg-blue-100 text-blue-700',
  Cancelled: 'bg-red-100 text-red-700',
};

function fmt(n: number, currency?: string) {
  if (!currency) return n.toFixed(2);
  return new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 2 }).format(n);
}

// ── Column definitions ──────────────────────────────────────────────────────

interface ColDef {
  key: string;
  label: string;
  render: (o: Order, extra: { displayName: (s: string) => string; parentNickname: (s: string) => string | null; scOrderUrl: (m: string, id: string) => string }) => React.ReactNode;
  align?: 'left' | 'right';
  className?: string;
  defaultVisible?: boolean;
}

const ALL_COLUMNS: ColDef[] = [
  { key: 'order_id', label: 'Order ID', defaultVisible: true, render: (o, { scOrderUrl }) => (
    <a href={scOrderUrl(o.marketplace, o.order_id)} target="_blank" rel="noopener noreferrer"
      className="text-brand-600 hover:text-brand-800 hover:underline font-mono text-xs">{o.order_id}</a>
  )},
  { key: 'date', label: 'Date', defaultVisible: true, className: 'whitespace-nowrap text-gray-600',
    render: o => o.purchase_date?.slice(0, 10) },
  { key: 'status', label: 'Status', defaultVisible: true, render: o => (
    <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[o.status] ?? 'bg-gray-100 text-gray-600'}`}>
      {o.status}
    </span>
  )},
  { key: 'market', label: 'Market', defaultVisible: true, className: 'text-gray-600',
    render: o => MARKETPLACE_LABELS[o.marketplace] ?? o.marketplace },
  { key: 'sku', label: 'SKU', defaultVisible: true, className: 'text-xs',
    render: (o, { displayName, parentNickname }) => {
      const pn = parentNickname(o.sku);
      return (
        <div title={o.sku}>
          {pn && <span className="text-[10px] text-gray-400 block">{pn}</span>}
          <span>{displayName(o.sku)}</span>
        </div>
      );
    }},
  { key: 'asin', label: 'ASIN', defaultVisible: false, className: 'font-mono text-xs',
    render: o => o.asin },
  { key: 'product', label: 'Product', defaultVisible: true, className: 'max-w-[200px] truncate text-gray-600',
    render: o => <span title={o.product_name}>{o.product_name}</span> },
  { key: 'qty', label: 'Qty', align: 'right', defaultVisible: true, className: 'tabular-nums',
    render: o => o.quantity },
  { key: 'qty_shipped', label: 'Shipped', align: 'right', defaultVisible: false, className: 'tabular-nums',
    render: o => o.quantity_shipped },
  { key: 'price', label: 'Price', align: 'right', defaultVisible: true, className: 'tabular-nums',
    render: o => <>{fmt(o.item_price, o.currency)}<span className="ml-1 text-[10px] text-gray-400">{o.currency}</span></> },
  { key: 'tax', label: 'Tax', align: 'right', defaultVisible: false, className: 'tabular-nums',
    render: o => fmt(o.item_tax, o.currency) },
  { key: 'shipping', label: 'Shipping', align: 'right', defaultVisible: false, className: 'tabular-nums',
    render: o => fmt(o.shipping_price, o.currency) },
  { key: 'fulfillment', label: 'Fulfillment', defaultVisible: false, className: 'text-gray-600',
    render: o => o.fulfillment_channel },
  { key: 'buyer', label: 'Buyer', defaultVisible: true, className: 'max-w-[120px] truncate text-gray-600',
    render: o => <span title={o.buyer_name}>{o.buyer_name}</span> },
  { key: 'location', label: 'Location', defaultVisible: true, className: 'whitespace-nowrap text-gray-500 text-xs',
    render: o => [o.ship_city, o.ship_state, o.ship_country].filter(Boolean).join(', ') },
];

const ORDERS_COL_STORAGE_KEY = 'sellvana_orders_columns';

function loadOrdersColumnConfig(): { visible: string[]; order: string[] } {
  try {
    const raw = localStorage.getItem(ORDERS_COL_STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return {
    visible: ALL_COLUMNS.filter(c => c.defaultVisible).map(c => c.key),
    order: ALL_COLUMNS.map(c => c.key),
  };
}

function saveOrdersColumnConfig(cfg: { visible: string[]; order: string[] }) {
  localStorage.setItem(ORDERS_COL_STORAGE_KEY, JSON.stringify(cfg));
}

function ColumnConfig({ config, onChange }: {
  config: { visible: string[]; order: string[] };
  onChange: (cfg: { visible: string[]; order: string[] }) => void;
}) {
  const [open, setOpen] = useState(false);
  const [dragIdx, setDragIdx] = useState<number | null>(null);

  const ordered = config.order.map(k => ALL_COLUMNS.find(c => c.key === k)!).filter(Boolean);

  const toggle = (key: string) => {
    const vis = config.visible.includes(key)
      ? config.visible.filter(k => k !== key)
      : [...config.visible, key];
    const next = { ...config, visible: vis };
    onChange(next); saveOrdersColumnConfig(next);
  };

  const move = (from: number, to: number) => {
    const order = [...config.order];
    const [item] = order.splice(from, 1);
    order.splice(to, 0, item);
    const next = { ...config, order };
    onChange(next); saveOrdersColumnConfig(next);
  };

  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)}
        className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 flex items-center gap-1">
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
        </svg>
        Columns
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full z-50 mt-1 w-56 rounded-xl border border-gray-200 bg-white shadow-lg p-2 max-h-80 overflow-y-auto">
            <p className="text-[10px] uppercase text-gray-400 font-semibold px-2 py-1">Drag to reorder</p>
            {ordered.map((col, i) => (
              <div key={col.key}
                draggable
                onDragStart={() => setDragIdx(i)}
                onDragOver={e => e.preventDefault()}
                onDrop={() => { if (dragIdx !== null && dragIdx !== i) move(dragIdx, i); setDragIdx(null); }}
                className={`flex items-center gap-2 rounded-lg px-2 py-1.5 cursor-grab hover:bg-gray-50 ${dragIdx === i ? 'opacity-50' : ''}`}>
                <input type="checkbox" checked={config.visible.includes(col.key)}
                  onChange={() => toggle(col.key)}
                  className="rounded border-gray-300 text-brand-600 h-3.5 w-3.5" />
                <span className="text-xs text-gray-700 flex-1">{col.label}</span>
                <svg className="w-3 h-3 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                </svg>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────────

interface CatalogProduct { asin: string; sku: string; title: string }

interface ProductGroup {
  label: string;
  asins: string[];
  products: CatalogProduct[];
}

function groupProducts(products: CatalogProduct[]): ProductGroup[] {
  // Filter out empty titles, deduplicate by ASIN
  const seen = new Set<string>();
  const filtered = products.filter(p => {
    if (!p.title?.trim() || seen.has(p.asin)) return false;
    seen.add(p.asin);
    return true;
  });

  // Group by short label
  const groups = new Map<string, CatalogProduct[]>();
  for (const p of filtered) {
    const label = p.title.includes('Walkie') ? 'Walkie Talkies'
      : p.title.includes('CarPlay') || p.title.includes('Android') ? 'CarPlay Adapter'
      : p.title.slice(0, 30);
    if (!groups.has(label)) groups.set(label, []);
    groups.get(label)!.push(p);
  }
  return [...groups.entries()].map(([label, prods]) => ({
    label, asins: [...new Set(prods.map(p => p.asin))], products: prods,
  }));
}

export function SalesOrders() {
  const [data, setData] = useState<OrdersResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const { displayName, parentNickname } = useNicknames();
  const [start, setStart] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [end, setEnd] = useState(() => new Date().toISOString().slice(0, 10));
  const [marketplace, setMarketplace] = useState('');
  const [status, setStatus] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedAsin, setSelectedAsin] = useState('');
  const [productGroups, setProductGroups] = useState<ProductGroup[]>([]);
  const [availableMarkets, setAvailableMarkets] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [activeDays, setActiveDays] = useState<number | null>(30);
  const [colConfig, setColConfig] = useState(loadOrdersColumnConfig);

  const visibleCols = colConfig.order
    .filter(k => colConfig.visible.includes(k))
    .map(k => ALL_COLUMNS.find(c => c.key === k)!)
    .filter(Boolean);

  // Load available marketplaces + product catalog once
  useEffect(() => {
    api.post<{ rows: Array<{ marketplace: string }> }>(
      '/admin/query',
      { sql: "SELECT DISTINCT marketplace FROM amazon_orders WHERE marketplace IS NOT NULL ORDER BY marketplace" },
    ).then(r => {
      setAvailableMarkets(r.rows.map(row => row.marketplace));
    }).catch(() => {});

    api.post<{ rows: Array<{ asin: string; sku: string; title: string }> }>(
      '/admin/query',
      { sql: "SELECT c.asin, c.sku, c.title FROM amazon_catalog c WHERE c.asin IS NOT NULL AND c.title IS NOT NULL AND c.title != '' AND c.sku NOT LIKE 'amzn.gr.%' GROUP BY c.asin ORDER BY c.title" },
    ).then(r => {
      setProductGroups(groupProducts(r.rows));
    }).catch(() => {});
  }, []);

  // Get variations for selected group
  const activeGroup = productGroups.find(g => g.label === selectedGroup);
  const effectiveAsin = selectedAsin || (activeGroup ? activeGroup.asins.join(',') : '');

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ start, end, page: String(page), limit: '50' });
    if (marketplace) params.set('marketplace', marketplace);
    if (status) params.set('status', status);
    if (effectiveAsin) params.set('asin', effectiveAsin);
    api.get<OrdersResponse>(`/sales/orders?${params}`)
      .then(setData)
      .finally(() => setLoading(false));
  }, [start, end, marketplace, status, effectiveAsin, page]);

  useEffect(() => { load(); }, [load]);

  const totalPages = data ? Math.ceil(data.total / data.limit) : 1;

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex flex-wrap items-end gap-4">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-600">From</label>
          <input type="date" value={start} onChange={e => { setStart(e.target.value); setPage(1); setActiveDays(null); }}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm" />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-600">To</label>
          <input type="date" value={end} onChange={e => { setEnd(e.target.value); setPage(1); setActiveDays(null); }}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm" />
        </div>
        <div className="flex gap-2">
          {[
            { key: 'today', label: 'Today', days: 0 },
            { key: 'yesterday', label: 'Yesterday', days: -1 },
            { key: '7d', label: '7d', days: 7 },
            { key: '14d', label: '14d', days: 14 },
            { key: '30d', label: '30d', days: 30 },
            { key: '90d', label: '90d', days: 90 },
          ].map(({ key, label, days }) => (
            <button key={key} onClick={() => {
              const today = new Date();
              if (days === 0) {
                const d = today.toISOString().slice(0, 10);
                setStart(d); setEnd(d);
              } else if (days === -1) {
                const y = new Date(today); y.setDate(y.getDate() - 1);
                const d = y.toISOString().slice(0, 10);
                setStart(d); setEnd(d);
              } else {
                const s = new Date(); s.setDate(s.getDate() - days);
                setStart(s.toISOString().slice(0, 10)); setEnd(today.toISOString().slice(0, 10));
              }
              setPage(1); setActiveDays(days);
            }} className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
              activeDays === days
                ? 'border-brand-500 bg-brand-50 text-brand-700'
                : 'border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}>
              {label}
            </button>
          ))}
        </div>
        {/* Marketplace buttons — only markets with orders */}
        <div className="flex gap-1.5">
          <button onClick={() => { setMarketplace(''); setPage(1); }}
            className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
              !marketplace ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}>All</button>
          {availableMarkets.map(id => (
            <button key={id} onClick={() => { setMarketplace(id === marketplace ? '' : id); setPage(1); }}
              className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
                marketplace === id ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}>{MARKETPLACE_LABELS[id] ?? id}</button>
          ))}
        </div>
        {/* Status */}
        <select value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}
          className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm">
          <option value="">All Statuses</option>
          <option value="Shipped">Shipped</option>
          <option value="Unshipped">Unshipped</option>
          <option value="Pending">Pending</option>
          <option value="Cancelled">Cancelled</option>
        </select>
        {/* Product group (parent ASIN) */}
        <select value={selectedGroup} onChange={e => { setSelectedGroup(e.target.value); setSelectedAsin(''); setPage(1); }}
          className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm">
          <option value="">All Products</option>
          {productGroups.map(g => (
            <option key={g.label} value={g.label}>{g.label} ({g.products.length})</option>
          ))}
        </select>
        {/* Variation (child ASIN) — only visible when a group is selected */}
        {activeGroup && activeGroup.products.length > 1 && (
          <select value={selectedAsin} onChange={e => { setSelectedAsin(e.target.value); setPage(1); }}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm">
            <option value="">All Variations</option>
            {activeGroup.products.map(p => {
              const variant = p.title.match(/\(([^)]+)\)$/)?.[1] ?? p.asin;
              return <option key={p.asin} value={p.asin}>{variant}</option>;
            })}
          </select>
        )}
        <ColumnConfig config={colConfig} onChange={setColConfig} />
      </div>

      {/* Results count */}
      {data && (
        <p className="text-sm text-gray-500">
          {data.total.toLocaleString()} orders found &middot; page {data.page} of {totalPages}
        </p>
      )}

      {loading ? (
        <div className="flex h-64 items-center justify-center text-gray-400">Loading...</div>
      ) : !data?.orders.length ? (
        <div className="flex h-64 items-center justify-center text-gray-400">No orders found</div>
      ) : (
        <>
          {/* Orders table */}
          <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  {visibleCols.map(c => (
                    <th key={c.key} className={`px-4 py-3 ${c.align === 'right' ? 'text-right' : ''}`}>{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.orders.map((o, i) => (
                  <tr key={`${o.order_id}-${i}`} className="hover:bg-gray-50">
                    {visibleCols.map(c => (
                      <td key={c.key} className={`px-4 py-2 ${c.align === 'right' ? 'text-right' : ''} ${c.className ?? ''}`}>
                        {c.render(o, { displayName, parentNickname, scOrderUrl })}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm disabled:opacity-40 hover:bg-gray-50">
                Previous
              </button>
              <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
              <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm disabled:opacity-40 hover:bg-gray-50">
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
